import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, LineChart, Cog, CheckCircle2, TrendingUp, Clock, Zap, HelpCircle, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '500TB+',
    label: 'Data Processed Daily',
    description: 'Across enterprise implementations and cloud platforms'
  },
  {
    icon: Database,
    value: '200+',
    label: 'Data Projects Delivered',
    description: 'Comprehensive data engineering solutions across industries'
  },
  {
    icon: Clock,
    value: '99.9%',
    label: 'System Uptime',
    description: 'Enterprise-grade reliability and performance'
  },
  {
    icon: CheckCircle2,
    value: '50+',
    label: 'Enterprise Integrations',
    description: 'Seamless connectivity across business systems'
  }
];

const services = [
  {
    title: 'Data Architecture & Infrastructure',
    icon: Database,
    items: [
      'Data Lake Implementation: Scalable storage solutions using Azure Data Lake, Databricks Delta Lake',
      'Data Warehouse Design: Enterprise data warehousing with Snowflake, Azure Synapse, SQL Server',
      'Real-Time Streaming: Apache Kafka, Azure Event Hubs for continuous data processing',
      'Cloud Migration: Legacy system modernization and cloud-native data platform development'
    ]
  },
  {
    title: 'ETL/ELT Pipeline Development',
    icon: Cog,
    items: [
      'Automated Data Pipelines: Azure Data Factory, SSIS, custom Python/Spark solutions',
      'Data Transformation: Complex business logic implementation with validation and quality assurance',
      'Incremental Loading: Efficient delta processing for large-scale data synchronization',
      'Error Handling: Robust exception management with automated retry logic and monitoring'
    ]
  },
  {
    title: 'Enterprise Integration Solutions',
    icon: Cloud,
    items: [
      'ERP System Integration: SAP, Oracle, Microsoft Dynamics automated data synchronization',
      'CRM Data Connectivity: Salesforce, HubSpot, Zoho real-time data flows',
      'API Development: RESTful APIs, GraphQL, and microservices architecture',
      'Legacy System Modernization: Mainframe connectivity and data bridge solutions'
    ]
  },
  {
    title: 'Data Quality & Governance',
    icon: Shield,
    items: [
      'Data Profiling: Comprehensive data quality assessment and anomaly detection',
      'Master Data Management: Single source of truth implementation across enterprise systems',
      'Data Lineage: Complete traceability from source systems through analytics platforms',
      'Compliance Framework: GDPR, HIPAA, SOX data governance and regulatory compliance'
    ]
  }
];

const techStack = [
  {
    category: 'Cloud Data Platforms',
    items: [
      { name: 'Microsoft Azure', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Amazon AWS', logo: '/tech-logos/aws.svg' },
      { name: 'Google Cloud', logo: '/tech-logos/google-cloud.svg' },
      { name: 'Snowflake', logo: '/tech-logos/snowflake.svg' }
    ]
  },
  {
    category: 'Big Data & Streaming',
    items: [
      { name: 'Apache Spark', logo: '/tech-logos/apache-spark.svg' },
      { name: 'Databricks', logo: '/tech-logos/databricks.svg' },
      { name: 'Apache Kafka', logo: '/tech-logos/apache-kafka.svg' },
      { name: 'Azure Stream Analytics', logo: '/tech-logos/azure-logo.svg' }
    ]
  },
  {
    category: 'Database Technologies',
    items: [
      { name: 'SQL Server', logo: '/tech-logos/sqlserver.png' },
      { name: 'PostgreSQL', logo: '/tech-logos/postgresql.png' },
      { name: 'MongoDB', logo: '/tech-logos/mongodb.svg' },
      { name: 'Redis', logo: '/tech-logos/redis.svg' }
    ]
  },
  {
    category: 'Integration & Development',
    items: [
      { name: 'Python', logo: '/tech-logos/python.svg' },
      { name: 'Power Platform', logo: '/tech-logos/power-bi.png' },
      { name: 'Node.js', logo: '/tech-logos/nodejs.svg' },
      { name: 'Docker', logo: '/tech-logos/docker.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare & Pharmaceuticals',
    icon: Shield,
    applications: [
      'Patient Data Integration (EHR, lab systems, medical devices)',
      'Clinical Analytics (research data aggregation and regulatory reporting)',
      'Population Health (public health data integration and analysis)',
      'Compliance Reporting (HIPAA, FDA automated audit trails)'
    ]
  },
  {
    title: 'Manufacturing & Supply Chain',
    icon: Cog,
    applications: [
      'IoT Data Processing (sensor data from production equipment)',
      'Supply Chain Analytics (vendor, inventory, and logistics data)',
      'Quality Management (production data integration for quality control)',
      'Operational Intelligence (real-time dashboards for production monitoring)'
    ]
  },
  {
    title: 'Financial Services & Insurance',
    icon: Database,
    applications: [
      'Risk Data Aggregation (portfolio, market, and operational risk data)',
      'Regulatory Reporting (SOX, Basel III, Solvency II automated compliance)',
      'Customer 360 (unified customer data platform across all touchpoints)',
      'Real-Time Analytics (trading data, market feeds, and transaction processing)'
    ]
  },
  {
    title: 'Retail & E-commerce',
    icon: LineChart,
    applications: [
      'Omnichannel Integration (point-of-sale, e-commerce, mobile, and social data)',
      'Customer Analytics (behavioral data integration for personalization)',
      'Inventory Intelligence (real-time stock levels across channels)',
      'Supply Chain Visibility (vendor, logistics, and demand data integration)'
    ]
  }
];

const successStories = [
  {
    tag: 'Insurance',
    title: 'Insurance Brokerage - Document Processing Pipeline',
    metrics: [
      '500+ daily statements automatically processed from multiple carriers',
      '95% reduction in manual data entry through intelligent automation',
      'Real-time CRM synchronization across all business operations'
    ],
    link: '/case-studies/insurance-automation'
  },
  {
    tag: 'Manufacturing',
    title: 'Manufacturing Company - Supply Chain Integration',
    metrics: [
      '88% forecast accuracy through integrated demand planning data',
      'Real-time visibility across SAP ERP, Siemens MES, and Oracle SCM',
      '40% safety stock reduction while maintaining service levels'
    ],
    link: '/case-studies/manufacturing-ai'
  },
  {
    tag: 'Logistics',
    title: 'Food Delivery Platform - Payment Data Processing',
    metrics: [
      '1000+ delivery partners real-time payment calculation and processing',
      'Multiple OMS integration (Yelo, Tookan) with automated reconciliation',
      '30-minute processing replacing 3-day manual workflows'
    ],
    link: '/case-studies/payment-automation'
  }
];

const methodology = [
  {
    title: 'Data Discovery & Assessment',
    description: 'Comprehensive data landscape analysis, quality assessment, and integration requirements gathering',
    icon: Database
  },
  {
    title: 'Architecture Design & Planning',
    description: 'Scalable data architecture design with technology selection and implementation roadmap development',
    icon: Cloud
  },
  {
    title: 'Agile Development & Implementation',
    description: 'Iterative development approach with continuous integration and automated testing protocols',
    icon: Cog
  },
  {
    title: 'Data Quality & Validation',
    description: 'Rigorous data validation, quality assurance, and performance optimization before production deployment',
    icon: Shield
  },
  {
    title: 'Monitoring & Maintenance',
    description: 'Comprehensive monitoring, alerting, and proactive maintenance for sustained performance and reliability',
    icon: TrendingUp
  }
];

const whyChooseUs = [
  {
    title: 'Enterprise-Grade Experience',
    description: 'Proven track record with complex data integration projects',
    icon: Shield
  },
  {
    title: 'Technology Leadership',
    description: 'Deep expertise in modern data stack technologies',
    icon: Database
  },
  {
    title: 'Industry Specialization',
    description: 'Sector-specific knowledge ensuring compliance and optimization',
    icon: LineChart
  },
  {
    title: 'Scalable Solutions',
    description: 'Future-proof architecture designed to grow with your business',
    icon: Cloud
  }
];

const faqs = [
  {
    question: 'How long does data engineering implementation take?',
    answer: 'Our rapid implementation approach delivers data engineering solutions in 4-8 weeks depending on complexity. ETL pipelines: 4-5 weeks. Data warehouse setup: 5-6 weeks. Full data platform with real-time streaming: 6-8 weeks. We\'re 60-80% faster than traditional consulting firms through agile methodologies and lean teams.'
  },
  {
    question: 'What makes Agility\'s implementation approach different?',
    answer: 'We deploy senior data engineers directly on your project no layers of account managers or junior staff. Our agile squads ship production-ready data pipelines in weeks, not months, with continuous delivery and real-time collaboration. Every week you see working data flows, not presentations.'
  },
  {
    question: 'Do you provide support after deployment?',
    answer: 'Yes, we provide 24/7 post-implementation support including continuous pipeline monitoring, performance optimization, and maintenance. Our support packages ensure your data infrastructure continues delivering value with proactive updates and issue resolution.'
  },
  {
    question: 'Which cloud platforms do you support?',
    answer: 'We have deep expertise in Azure (Data Factory, Synapse, Databricks), AWS (Glue, Redshift, EMR), and GCP (BigQuery, Dataflow). We also work with on-premise solutions like SQL Server, Oracle, and hybrid architectures combining cloud and on-premise systems.'
  }
];

const DataEngineeringSolution = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  return (
    <>
      <SEOHead
        title="Data Engineering & Cloud Infrastructure | Agility"
        description="Enterprise data engineering services. Data pipelines, cloud migration, ETL/ELT, and real-time data processing. 500TB+ data processed daily."
        keywords="data engineering, cloud infrastructure, data pipelines, ETL, data warehouse, big data, cloud migration"
        url="https://www.agilitytech.ai/solutions/data-engineering"
      />
      <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
            alt="Data Engineering Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
              <span className="text-teal-400 text-sm font-medium">Data Engineering & Integration</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Data Engineering & Integration Solutions
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
              Build robust, scalable data infrastructure that enables real-time analytics, seamless 
              integration, and enterprise-wide data democratization for informed decision-making.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your Data Strategy Consultation</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              <button
                className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium 
                  hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                  border border-white/20 hover:border-white/30"
              >
                <span>Download Data Engineering Guide</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <metric.icon className="h-6 w-6 text-teal-400" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                <p className="text-gray-400 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Engineering Services Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Data Engineering Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <service.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-300">
                      <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Technology Stack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="space-y-6">
                <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                <div className="grid grid-cols-1 gap-4">
                  {category.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                        border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                    >
                      <img
                        src={item.logo}
                        alt={`${item.name} logo`}
                        className="h-8 w-8 object-contain"
                      />
                      <span className="text-gray-300 font-medium">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Industry Applications
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                  transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6">
                  <industry.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                <ul className="space-y-3">
                  {industry.applications.map((app, idx) => (
                    <li key={idx} className="flex items-start space-x-3 text-gray-300">
                      <ArrowRight className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <span className="text-sm leading-relaxed">{app}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Success Stories Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Featured Success Stories
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                <h3 className="text-xl font-bold text-white mb-6">{story.title}</h3>
                <div className="space-y-4 mb-6">
                  {story.metrics.map((metric, idx) => (
                    <div key={idx} className="flex items-center space-x-3 text-gray-300">
                      <CheckCircle2 className="h-4 w-4 text-green-400" />
                      <span className="text-sm">{metric}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={story.link}
                  className="inline-flex items-center text-teal-400 hover:text-teal-300
                    transition-colors font-medium"
                >
                  <span>Read Full Case Study</span>
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Engineering Methodology Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Data Engineering Methodology
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {methodology.map((step, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center 
                    justify-center text-teal-400 font-bold">
                    {index + 1}
                  </div>
                  <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                    <step.icon className="h-5 w-5 text-teal-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Agility.AI Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
            Why Choose Agility.AI
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center 
                  justify-center mb-6 mx-auto">
                  <reason.icon className="h-6 w-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about our data engineering implementation process
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                  >
                    <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Database className="h-8 w-8 text-teal-400" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Modernize Your Data Infrastructure?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Build the Foundation for Data-Driven Success. Contact our data engineering specialists 
              to discover how modern data architecture can accelerate your analytics and business 
              intelligence initiatives.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Schedule Your Data Strategy Consultation</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default DataEngineeringSolution;